import React, { useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import { SelectChangeEvent, Stack, Typography } from "@mui/material";

import { SettingSubtitle } from "@app/analytics/account/components/styles";
import { getSettingDisabledOpts } from "@app/analytics/account/UserPreferences/utils";
import {
  DEFAULT_BOT_NAME_MODE,
  MIN_BOT_NAME_LENGTH,
  MAX_BOT_NAME_LENGTH,
  botNameValidationSchema,
} from "@app/analytics/account/utils";
import { ASSISTANT_NAME_CONFIG } from "@app/analytics/account/WorkspaceSettings/constants";
import TextField from "@app/analytics/components/TextField";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import type { BotNameMode } from "@app/analytics/types";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { User } from "@app/common/types";

import Dropdown from "@rui/Dropdown";
import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import { Row } from "@rui/Stack";

interface ReadAssistantNameSelectorProps {
  inWorkspace: boolean;
  user: User;
}

const getOptionDisabledOpts = (
  user: User | null,
  botNameMode: BotNameMode,
  canEditBotName: boolean
) => {
  if (!user) return { disabled: true };
  if (!user.editOptions) return { disabled: false };

  return getSettingDisabledOpts(
    canEditBotName,
    user.editOptions.botName.includes(botNameMode)
  );
};

const ReadAssistantNameSelector = ({
  inWorkspace,
  user,
}: ReadAssistantNameSelectorProps) => {
  const { canEditBotName } = useFeatureAccess();
  const { userSettings, updateUserSettings } = useUserSettings();

  const [customNameError, setCustomNameError] = useState<string>("");
  const [customName, setCustomName] = useState<string | null>(null);
  const [botNameMode, setBotNameMode] = useState<BotNameMode | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedBotNameMode =
    botNameMode ?? userSettings.botNameMode ?? DEFAULT_BOT_NAME_MODE;
  const customBotName = customName ?? userSettings.botNameModeCustomName;

  const handleCustomNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (customNameError) setCustomNameError("");
    setCustomName(event.target.value);
  };

  const handleBotNameModeChange = (event: SelectChangeEvent) => {
    const newMode = event.target.value as BotNameMode;
    setBotNameMode(newMode);

    // if custom, we don't want to update unless there is a name to update to
    if (newMode === "custom" && !customBotName) return;

    updateUserSettings.mutate({
      botNameMode: newMode,
      botNameModeCustomName: customBotName,
    });
  };

  const handleCustomNameSave = async () => {
    const isValidBotName = await botNameValidationSchema.isValid({
      botNameModeCustomName: customName,
    });

    if (!isValidBotName) {
      setCustomNameError(
        `Name must be between ${MIN_BOT_NAME_LENGTH} and ${MAX_BOT_NAME_LENGTH} characters.`
      );
      return;
    }

    // use state value to track this setting being saved, otherwise we might get a loading
    // state when a different setting is changed
    setSaving(true);
    return updateUserSettings.mutate(
      {
        botNameMode: "custom",
        botNameModeCustomName: customName!,
      },
      { onSettled: () => setSaving(false) }
    );
  };

  const { disabled: customNameInputDisabled } = getOptionDisabledOpts(
    user,
    "custom",
    canEditBotName
  );

  const disabled = getOptionDisabledOpts(
    user,
    selectedBotNameMode,
    canEditBotName
  );

  const showCustomBotNameInput = selectedBotNameMode === "custom";
  const enabledNameOptions = ASSISTANT_NAME_CONFIG.options.filter((option) =>
    option.isEnabled(inWorkspace)
  );

  const assistantNameOptions = enabledNameOptions.map((option) => ({
    value: option.id,
    label: option.displayName(user),
  }));

  return (
    <Stack sx={{ color: "shades.neutral.800" }} spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant="bodyPrimaryMed">
          {ASSISTANT_NAME_CONFIG.title}
        </Typography>
        <SettingSubtitle>{ASSISTANT_NAME_CONFIG.subtitle}</SettingSubtitle>
      </Stack>
      <Stack sx={{ maxWidth: "500px" }} spacing={1}>
        <Dropdown
          name="assistant-name"
          onChange={handleBotNameModeChange}
          selectedItem={getSelectedFromOptions(
            assistantNameOptions,
            selectedBotNameMode as string
          )}
          disabled={disabled.disabled}
          tooltip={disabled.tooltip}
          defaultValue={DEFAULT_BOT_NAME_MODE}
          sx={{ maxWidth: "500px" }}
        >
          {assistantNameOptions.map((item) => (
            <DropdownMenuItem
              key={item.value}
              {...item}
              isDefault={DEFAULT_BOT_NAME_MODE === item.value}
              selected={item.value === selectedBotNameMode}
            />
          ))}
        </Dropdown>
        {showCustomBotNameInput && (
          <Row spacing={1} sx={{ height: "44px" }}>
            <TextField
              sx={{ width: "100%" }}
              InputProps={{
                placeholder: "Type custom assistant name...",
                value: customBotName,
                sx: {
                  height: "40px",
                  "& input": { fontSize: "0.875rem" },
                },
              }}
              onChange={handleCustomNameChange}
              disabled={customNameInputDisabled}
              error={Boolean(customNameError)}
              helperText={customNameError}
            />
            {!customNameInputDisabled && (
              <LoadingButton
                variant="contained"
                onClick={handleCustomNameSave}
                sx={{ height: "40px", width: "70px" }}
                loading={saving}
              >
                Save
              </LoadingButton>
            )}
          </Row>
        )}
      </Stack>
    </Stack>
  );
};

export default ReadAssistantNameSelector;
